import React, { useState, useEffect, useRef } from 'react';
import { IoIosEye, IoIosEyeOff } from 'react-icons/io';
import { getCookie, setCookie, deleteCookie } from '../../utils/cookieUtils';
import { useAuth } from '../AuthContext';
import { Link, useNavigate } from 'react-router-dom';

// Cloudflare Turnstile bot protection — shared widget + site key, also used
// by the /start flow's slim signup (extracted 2026-08-27).
import TurnstileWidget, { TURNSTILE_SITE_KEY } from './TurnstileWidget';
import { cleanJoinCode } from '../../Join/joinLinks';

// The look (2026-10-28): both dropdowns are drawn like the teacher signup's
// select (authLook.css .al-select / .al-combo / .al-menu). Only their look
// changed; the keys, clicks and values work as before. labelId / inputId tie
// them to the visible label above them.

// Simple Dropdown Component (no search)
function SimpleDropdown({
  options,
  value,
  onChange,
  placeholder,
  error = false,
  name,
  disabled = false,
  labelId
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  // A field low on the screen opens its list below the fold, under the
  // sticky Sign up bar: scroll just enough to show the whole list.
  useEffect(() => {
    if (isOpen && menuRef.current) menuRef.current.scrollIntoView?.({ block: 'nearest' });
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(value ? options.findIndex(opt => opt.value === value) : 0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < options.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : options.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleSelectOption(options[highlightedIndex].value);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleSelectOption = (optionValue) => {
    onChange({ target: { name: name || 'hearAboutUs', value: optionValue } });
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';

  return (
    <div ref={dropdownRef} className="al-dd">
      <div
        className={`al-select${displayValue ? '' : ' is-empty'}${isOpen ? ' is-open' : ''}${error ? ' al-invalid' : ''}${disabled ? ' is-disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={labelId ? `${labelId} ${labelId}-value` : undefined}
      >
        <span id={labelId ? `${labelId}-value` : undefined}>{displayValue || placeholder}</span>
      </div>

      {isOpen && !disabled && (
        <ul ref={menuRef} className="al-menu" role="listbox" aria-labelledby={labelId}>
          {options.map((option, index) => (
            <li
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              className={`al-menu-opt${index === highlightedIndex ? ' is-hi' : ''}${option.value === value ? ' is-on' : ''}`}
              onClick={() => handleSelectOption(option.value)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Searchable Dropdown Component
function SearchableDropdown({
  options,
  value,
  onChange,
  placeholder,
  error,
  disabled = false,
  inputId
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  // Same as SimpleDropdown: show the whole list when it opens low on the screen.
  useEffect(() => {
    if (isOpen && menuRef.current) menuRef.current.scrollIntoView?.({ block: 'nearest' });
  }, [isOpen]);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleSelectOption = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setHighlightedIndex(-1);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const displayValue = value && value !== '' ? value : '';

  return (
    <div ref={dropdownRef} className="al-dd">
      <div onClick={() => !disabled && setIsOpen(!isOpen)}>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`al-combo${error ? ' al-invalid' : ''}`}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-controls={inputId ? `${inputId}-list` : undefined}
          aria-expanded={isOpen && !disabled}
          aria-autocomplete="list"
        />
      </div>

      {isOpen && !disabled && (
        <ul ref={menuRef} className="al-menu" role="listbox" id={inputId ? `${inputId}-list` : undefined}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={option}
                role="option"
                aria-selected={option === value}
                className={`al-menu-opt${index === highlightedIndex ? ' is-hi' : ''}${option === value ? ' is-on' : ''}`}
                onClick={() => handleSelectOption(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {option}
              </li>
            ))
          ) : (
            <li className="al-menu-empty">
              No schools found
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// One "How are you signing up?" card: the survey's option card (.ts-opt).
// The radio is the same input as before (name signupType), only drawn as a card.
function TypeOption({ value, checked, onChange, title, note }) {
  return (
    <label className={`ts-opt${checked ? ' is-on' : ''}`}>
      <input type="radio" name="signupType" value={value} checked={checked} onChange={onChange} className="ts-radio-input" />
      <span className="ts-opt-text">
        <span className="ts-opt-title">{title}</span>
        {note && <span className="ts-opt-note">{note}</span>}
      </span>
      <span className="ts-radio" aria-hidden="true" />
    </label>
  );
}

function SignUpForm({ onSubmit, isSubmitting, formSubmitting, errorMessage, signInLink }) {
  const navigate = useNavigate();
  const { handleAPIResponse } = useAuth();
  const [formData, setFormData] = useState({
    // firstname/lastname are UI-only (Sandy 2026-08-09): they join into
    // fullname below, so Cognito custom:FullName and Dynamo still receive
    // ONE full-name string — no backend change.
    firstname: '',
    lastname: '',
    fullname: '',
    username: '',
    password: '',
    confirmPassword: '',
    hearAboutUs: '',
    race: '',
    gender: '',
    school: 'No School',
    role: 'Student',
    grade: '',
    authorizationCode: '',
    signupType: null, // 'partner', 'teacher', or 'individual'
    promoCode: '',
    classCode: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [showAuthCodeInput, setShowAuthCodeInput] = useState(false);
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [formError, setFormError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  // Turnstile tokens are single-use: after a failed submit, reset the widget
  // so the next attempt gets a fresh token.
  useEffect(() => {
    if ((errorMessage || formError) && TURNSTILE_SITE_KEY && window.turnstile) {
      try {
        window.turnstile.reset();
        setCaptchaToken('');
      } catch (e) { /* widget not rendered */ }
    }
  }, [errorMessage, formError]);

  // The Sign up button stays in reach at the bottom of the screen (the
  // look's sticky actions), so a new error can land out of view on a long
  // form: bring it up, as the teacher signup does. Look only; no logic.
  const errorRef = useRef(null);
  useEffect(() => {
    if ((errorMessage || formError) && errorRef.current) errorRef.current.scrollIntoView?.({ block: 'center' });
  }, [errorMessage, formError]);

  // School options (2026-08-05: Clear Creek only for the V3 rollout; the old
  // partner list returns as those schools onboard onto the new stack)
  // Erie back for 2026-27 (Sandy 2026-08-16): all Erie-specific fields
  // (grade, English/Advisory teacher pickers) key off school === 'Erie'.
  // Jefferson, Bear Creek, Columbine (Sandy 2026-09-10): rostered on
  // 2026-09-10 as Jefferson2025 / BearCreek2025 / Columbine2026. The
  // dropdown label is what Cognito custom:School receives; the lambdas
  // (addUser-staging, claude-user-api) map it to that school string.
  // Thornton (Sandy 2026-09-30): rostered as "Thorton2025" (sic, one n, the
  // string last year's accounts use); users see the correct spelling and the
  // lambdas map the "Thornton" label to Thorton2025.
  const schoolOptions = [
    "Alamosa",
    "Bear Creek",
    "Chatfield",
    "Clear Creek",
    "Columbine",
    "Erie",
    "Jefferson",
    "Thornton",
  ];

  const erieGradeOptions = [
    { value: "9", label: "9th Grade" },
    { value: "10", label: "10th Grade" },
    { value: "11", label: "11th Grade" }
  ];

  const alamosaGradeOptions = [
    { value: "9", label: "9th Grade" },
    { value: "10", label: "10th Grade" },
    { value: "11", label: "11th Grade" }
  ];

  // Clear Creek students pick their grade at signup (Sandy 2026-08-08);
  // stored in Cognito as custom:Grade and not editable afterwards.
  const clearCreekGradeOptions = [
    { value: "9", label: "9th Grade" },
    { value: "10", label: "10th Grade" },
    { value: "11", label: "11th Grade" }
  ];

  // Jefferson / Bear Creek / Columbine / Chatfield / Thornton rosters carry all four grades.
  const allGradeOptions = [
    { value: "9", label: "9th Grade" },
    { value: "10", label: "10th Grade" },
    { value: "11", label: "11th Grade" },
    { value: "12", label: "12th Grade" }
  ];

  // Schools whose students pick a grade at signup -> the grade list they see.
  const schoolGradeOptions = {
    "Erie": erieGradeOptions,
    "Jefferson": allGradeOptions,
    "Bear Creek": allGradeOptions,
    "Columbine": allGradeOptions,
    "Chatfield": allGradeOptions,
    "Thornton": allGradeOptions,
    "Alamosa": alamosaGradeOptions,
    "Clear Creek": clearCreekGradeOptions,
  };

  // Erie teacher pickers removed (Sandy 2026-08-16): students join their
  // teachers' classes with join codes after signup instead.
  const hearAboutUsOptions = [
    { value: "School", label: "School" },
    { value: "Reddit", label: "Reddit" },
    { value: "Google", label: "Google" },
    { value: "Facebook", label: "Facebook" },
    { value: "Instagram", label: "Instagram" },
    { value: "TikTok", label: "TikTok" },
    { value: "Personal Referral", label: "Personal Referral" },
    { value: "Affiliate Program", label: "Affiliate Program" },
    { value: "Other", label: "Other" }
  ];

  const roleOptions = [
    { value: "Student", label: "Student" },
    { value: "Teacher", label: "Teacher" },
    // Admin onboarding (Sandy 2026-08-17): gated by a per-school Admin code
    // in the teacherAuthorizationCode lambda, same flow as Teacher.
    { value: "Admin", label: "Admin" },
  ];

  useEffect(() => {
    setPasswordsMatch(formData.password === formData.confirmPassword);
  }, [formData.password, formData.confirmPassword]);

  useEffect(() => {
    if (formData.signupType !== 'partner') {
      setFormData(prevData => ({
        ...prevData,
        school: 'No School',
        role: 'Student',
        grade: ''
      }));
      setShowAuthCodeInput(false);
    } else {
      setShowAuthCodeInput(true);
    }
  }, [formData.signupType]);

  useEffect(() => {
    const gradeOptions = schoolGradeOptions[formData.school];
    if (gradeOptions && formData.role === 'Student') {
      // The student picks the grade themselves; clear anything not in that school's list.
      const isValidGrade = gradeOptions.some(option => option.value === formData.grade);
      if (!isValidGrade) {
        setFormData(prevData => ({
          ...prevData,
          grade: ''
        }));
      }
    } else if (formData.school === 'No School') {
      setFormData(prevData => ({
        ...prevData,
        grade: ''
      }));
    } else if (formData.role === 'Student') {
      setFormData(prevData => ({
        ...prevData,
        grade: '11'
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        grade: ''
      }));
    }
  }, [formData.school, formData.role]);

  // useEffect(() => {
  //   const affiliateId = getCookie('affiliateId');
  //   if (affiliateId) {
  //     setFormData(prevData => ({ ...prevData, promoCode: affiliateId }));
  //     setShowPromoCode(true);
  //   }
  // }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'promoCode') {
      setFormData(prevData => ({ ...prevData, [name]: value.toUpperCase() }));
    } else {
      setFormData(prevData => ({ ...prevData, [name]: value }));
    }
  };

  // First/last name inputs (Sandy 2026-08-09): every keystroke re-joins the
  // derived fullname so validation and the backend contract stay unchanged.
  const handleNamePart = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      next.fullname = `${next.firstname || ''} ${next.lastname || ''}`.trim();
      return next;
    });
  };

  const handleSchoolChange = (selectedSchool) => {
    setFormData(prevData => ({ ...prevData, school: selectedSchool }));
  };

  const handleSignupTypeChange = (e) => {
    const signupType = e.target.value;
    setFormData(prevData => ({
      ...prevData,
      signupType,
      school: signupType === 'partner' ? '' : 'No School',
      role: signupType === 'partner' ? '' : 'Student',
      classCode: signupType === 'teacher' ? prevData.classCode : '',
      authorizationCode: signupType === 'partner' ? prevData.authorizationCode : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!passwordsMatch) {
      setFormError("Passwords don't match");
      return;
    }

    setFormError('');

    // // Validate promo code if present
    // if (formData.promoCode && (!verificationStatus || !verificationStatus.success)) {
    //   const verificationSucceeded = await handleVerifyPromoCode();
    //   if (!verificationSucceeded) {
    //     alert("The promo code you entered is invalid. Please correct it or remove it to continue.");
    //     return;
    //   }
    // }

    // Validate class code if present (before Cognito signup)
    let v3JoinCode = null;
    if (formData.signupType === 'teacher') {
      if (!formData.classCode || formData.classCode.trim().length === 0) {
        setFormError('Please enter a class code');
        return;
      }

      const normalizedCode = formData.classCode.toUpperCase().trim();

      try {
        const response = await fetch(`https://50et7rsdo1.execute-api.us-east-1.amazonaws.com/validate-class-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ classCode: normalizedCode }),
        });

        const data = await response.json();

        // Not an old Teacher Program code (2026-10-28): a class code from the
        // teacher dashboard (claude-teacher-api join code). The account is
        // made with the class-code marker; the PreSignUp trigger refuses a
        // code that matches no class, PostConfirmation gives the student the
        // class's school, and signup.jsx joins the class after sign-in.
        if (response.status === 404) {
          v3JoinCode = cleanJoinCode(formData.classCode);
        } else if (!response.ok) {
          if (response.status === 403) {
            setFormError(data.error || 'This class is no longer accepting students.');
          } else {
            setFormError('Unable to validate class code. Please try again.');
          }
          return;
        } else {
          if (!data.valid || !data.licensesAvailable) {
            if (!data.licensesAvailable) {
              setFormError('This class has reached its student limit. Please contact your teacher.');
            } else {
              setFormError('Invalid class code.');
            }
            return;
          }

          // Validation successful - update form data with class info
          formData.school = 'Unknown'; // Lambda will set to CUSTOM-{teacherId}
          formData.role = 'Student';
          if (data.grade) {
            formData.grade = data.grade;
          }
        }
      } catch (error) {
        setFormError('Network error. Please check your connection and try again.');
        return;
      }
    }

    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setFormError('Please wait a moment for the security check to finish, then try again.');
      return;
    }

    onSubmit({ ...formData, captchaToken, ...(v3JoinCode && { v3JoinCode }) });
  };

  // const handleCancelPromoCode = () => {
  //   deleteCookie('affiliateId');
  //   setFormData(prevData => ({ ...prevData, promoCode: '' }));
  //   setVerificationStatus(null);
  // };

  // const handleVerifyPromoCode = async () => {
  //   if (!formData.promoCode) {
  //     alert("Please enter a promo code to verify");
  //     return false;
  //   }

  //   setVerifying(true);

  //   const requestData = {
  //     affiliateId: formData.promoCode
  //   };

  //   try {
  //     const apiEndpoint = 'https://fm407nxajh.execute-api.us-west-2.amazonaws.com/verifyAffiliateID';

  //     const response = await fetch(apiEndpoint, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify(requestData),
  //     });
  //     const handledResponse = await handleAPIResponse(response, requestData);

  //     if (!handledResponse.ok) {
  //       const errorMessage = await handledResponse.text();
  //       console.error(`HTTP error! Status: ${handledResponse.status}. Message: ${errorMessage}`);
  //       navigate('/signup');
  //       throw new Error('An error occurred while fetching previous assignments.');
  //     }

  //     const data = await handledResponse.json();

  //     if (data.success && data.isValid) {
  //       setCookie('affiliateId', formData.promoCode, 30);
  //       setVerificationStatus({ success: true, message: "Successfully verified" });
  //       return true;
  //     } else {
  //       setVerificationStatus({ success: false, message: "Promo code invalid" });
  //       return false;
  //     }
  //   } catch (err) {
  //     console.error('Error verifying promo code:', err);
  //     setVerificationStatus({ success: false, message: "Promo code invalid" });
  //     return false;
  //   } finally {
  //     setVerifying(false);
  //   }
  // };

  const shouldShowGradeSelection = !!schoolGradeOptions[formData.school] && formData.role === 'Student';

  const getGradeOptions = () => schoolGradeOptions[formData.school] || erieGradeOptions;

  // a field left empty after a submit (same tests as before)
  const missing = (empty) => (empty ? ' al-invalid' : '');
  const missingClass = (empty) => (empty ? 'al-invalid' : undefined);
  const isPartner = formData.signupType === 'partner';
  const isTeacher = formData.signupType === 'teacher';

  return (
    <form onSubmit={handleSubmit}>
      <h1 className="ts-title">Sign up</h1>
      <p className="ts-sub">Make your Brain Buffs account.</p>

      <div className="ts-fieldset ts-form-fields">
        {/* First/last split (Sandy 2026-08-09): joined into formData.fullname
            on every keystroke, so validation and the backend contract
            (custom:FullName -> Dynamo) are unchanged. */}
        <div className="ts-name-row">
          <div className="ts-field">
            <label className="ts-field-label" htmlFor="al-first">First name</label>
            <input
              id="al-first"
              type="text"
              name="firstname"
              autoComplete="given-name"
              value={formData.firstname}
              onChange={handleNamePart}
              className={missingClass(formSubmitting && isPartner && !formData.firstname)}
            />
            {formSubmitting && isPartner && !formData.firstname &&
              <span className="al-field-error">Required</span>}
          </div>
          <div className="ts-field">
            <label className="ts-field-label" htmlFor="al-last">Last name</label>
            <input
              id="al-last"
              type="text"
              name="lastname"
              autoComplete="family-name"
              value={formData.lastname}
              onChange={handleNamePart}
              className={missingClass(formSubmitting && isPartner && !formData.lastname)}
            />
            {formSubmitting && isPartner && !formData.lastname &&
              <span className="al-field-error">Required</span>}
          </div>
        </div>

        {/* Always "School email" (Sandy 2026-09-10): the account must be the
            student's or teacher's school address for every signup type. */}
        <div className="ts-field">
          <label className="ts-field-label" htmlFor="al-email">School email</label>
          <input
            id="al-email"
            className={missingClass(formSubmitting && !formData.username)}
            type="email"
            name="username"
            inputMode="email"
            autoComplete="email"
            value={formData.username}
            onChange={handleChange}
          />
          {formSubmitting && !formData.username && <span className="al-field-error">Required</span>}
        </div>

        <div className="ts-field">
          <label className="ts-field-label" htmlFor="al-password">Password</label>
          <div className="al-pw">
            <input
              id="al-password"
              className={missingClass(formSubmitting && !formData.password)}
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="al-eye"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <IoIosEyeOff /> : <IoIosEye />}
            </button>
          </div>
          {formSubmitting && !formData.password && <span className="al-field-error">Required</span>}
        </div>

        <div className="ts-field">
          <label className="ts-field-label" htmlFor="al-confirm">Confirm password</label>
          <div className="al-pw">
            <input
              id="al-confirm"
              className={missingClass(formSubmitting && !formData.confirmPassword)}
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="al-eye"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <IoIosEyeOff /> : <IoIosEye />}
            </button>
          </div>
          {formSubmitting && !formData.confirmPassword && <span className="al-field-error">Required</span>}
        </div>

        {/* <div className="mb-2">
          <SimpleDropdown
            options={hearAboutUsOptions}
            value={formData.hearAboutUs}
            onChange={handleChange}
            placeholder="How did you hear about us?"
          />
        </div> */}

        {/* The three ways to sign up, as the survey's option cards. A picked
            card opens its own fields inside it (.ts-optgroup). */}
        <div className="al-type" role="radiogroup" aria-labelledby="al-type-label">
          <span className="ts-field-label" id="al-type-label">How are you signing up?</span>
          <div className="ts-opts">
            <div className={`ts-optgroup${isPartner ? ' is-on' : ''}`}>
              <TypeOption
                value="partner"
                checked={isPartner}
                onChange={handleSignupTypeChange}
                title="With a School"
                note="Your school gave you an authorization code"
              />
              {isPartner && (
                <div className="ts-request">
                  <div className="ts-field">
                    <label className="ts-field-label" htmlFor="al-school">School</label>
                    <SearchableDropdown
                      inputId="al-school"
                      options={schoolOptions}
                      value={formData.school}
                      onChange={handleSchoolChange}
                      placeholder="Search for your school"
                      error={formSubmitting && !formData.school}
                    />
                    {formSubmitting && !formData.school && <span className="al-field-error">Required</span>}
                  </div>

                  <div className="ts-field">
                    <span className="ts-field-label" id="al-role">Role</span>
                    <SimpleDropdown
                      labelId="al-role"
                      options={roleOptions}
                      value={formData.role}
                      onChange={handleChange}
                      placeholder="Choose your role"
                      name="role"
                      error={formSubmitting && !formData.role}
                    />
                    {formSubmitting && !formData.role && <span className="al-field-error">Required</span>}
                  </div>

                  {shouldShowGradeSelection && (
                    <div className="ts-field">
                      <span className="ts-field-label" id="al-grade">Grade</span>
                      <SimpleDropdown
                        labelId="al-grade"
                        options={getGradeOptions()}
                        value={formData.grade}
                        onChange={handleChange}
                        placeholder="Choose your grade"
                        name="grade"
                        error={formSubmitting && !formData.grade}
                      />
                      {formSubmitting && !formData.grade && <span className="al-field-error">Required</span>}
                    </div>
                  )}

                  {showAuthCodeInput && (
                    <div className="ts-field">
                      <label className="ts-field-label" htmlFor="al-authcode">Authorization code</label>
                      <input
                        id="al-authcode"
                        type="text"
                        name="authorizationCode"
                        value={formData.authorizationCode}
                        onChange={handleChange}
                        className={missingClass(formSubmitting && !formData.authorizationCode)}
                      />
                      {formSubmitting && !formData.authorizationCode && <span className="al-field-error">Required</span>}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={`ts-optgroup${isTeacher ? ' is-on' : ''}`}>
              <TypeOption
                value="teacher"
                checked={isTeacher}
                onChange={handleSignupTypeChange}
                title="With a Teacher"
                note="Your teacher gave you a class code"
              />
              {isTeacher && (
                <div className="ts-request">
                  <div className="ts-field">
                    <label className="ts-field-label" htmlFor="al-classcode">Class code</label>
                    <input
                      id="al-classcode"
                      type="text"
                      name="classCode"
                      autoComplete="off"
                      value={formData.classCode}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase().trim();
                        setFormData(prevData => ({ ...prevData, classCode: value }));
                      }}
                      maxLength={6}
                      aria-describedby="al-classcode-hint"
                      className={`ts-code${missing(formSubmitting && !formData.classCode)}`}
                    />
                    {formSubmitting && !formData.classCode
                      ? <span className="al-field-error" id="al-classcode-hint">Please enter a class code</span>
                      : <span className="ts-hint" id="al-classcode-hint">Enter the 6-character code (e.g., ABC123)</span>}
                  </div>
                </div>
              )}
            </div>

            <TypeOption
              value="individual"
              checked={formData.signupType === 'individual'}
              onChange={handleSignupTypeChange}
              title="Non-School Sign Up"
              note="Sign up on your own"
            />
          </div>
          {formSubmitting && formData.signupType === null &&
            <span className="al-field-error">Required</span>}
        </div>
      </div>

      {/* <div className="text-center mb-1.5">
        <button
          type="button"
          onClick={() => setShowPromoCode(!showPromoCode)}
          className="bg-transparent border-0 text-primary-500 underline cursor-pointer font-normal"
        >
          Have a promo code?
        </button>
      </div> */}

      {/* {showPromoCode && (
        <div className="flex flex-col items-center justify-center mb-2 max-w-sm w-[98%] sm:w-11/12 mx-auto">
          <div className="flex w-full gap-2.5 mb-1 relative">
            <div className="relative flex-1">
              {formData.promoCode && (
                <button
                  type="button"
                  onClick={handleCancelPromoCode}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 bg-transparent border-0 cursor-pointer text-error-500 text-xl flex items-center justify-center z-10"
                >
                  <IoIosClose />
                </button>
              )}
              <input
                type="text"
                name="promoCode"
                placeholder="Enter promo code"
                value={formData.promoCode}
                onChange={handleChange}
                className={`p-3 border border-neutral-300 rounded-md text-base w-full text-black bg-white ${formData.promoCode ? 'pr-9' : 'pr-3'
                  }`}
              />
            </div>

            <button
              type="button"
              onClick={handleVerifyPromoCode}
              disabled={verifying}
              className={`
                bg-primary-400 text-white px-4 py-3 border-0 rounded-md shadow-md transition-colors duration-200
                ${verifying ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-primary-500'}
              `}
            >
              {verifying ? (
                <span>Verifying...</span>
              ) : (
                verificationStatus && verificationStatus.success ? 'Verified' : 'Verify'
              )}
            </button>
          </div>

          {verificationStatus && (
            <div className={`mt-1 text-sm text-center w-full ${verificationStatus.success ? 'text-primary-500' : 'text-error-500'
              }`}>
              {verificationStatus.message}
            </div>
          )}
        </div>
      )} */}

      {TURNSTILE_SITE_KEY && <TurnstileWidget onToken={setCaptchaToken} className="al-turnstile" />}

      <p className="al-legal">
        By signing up you agree to our <a href="https://www.brainbuffs.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, <a href="https://www.brainbuffs.com/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="https://www.brainbuffs.com/acceptable-use" target="_blank" rel="noopener noreferrer">Acceptable Use Policy</a>.
      </p>
      {(errorMessage || formError) && (
        <p ref={errorRef} className="ts-error" role="alert">
          {formError ? formError : errorMessage}
        </p>
      )}
      {/* Sign up, and Log in beside it (Sandy 2026-10-29); short words, so
          the pair stays one row on a phone and the sticky bar stays low. */}
      <div className="ts-actions al-pair al-pair--short">
        <button type="submit" className="ts-btn" disabled={isSubmitting}>
          {isSubmitting ? 'Signing up…' : 'Sign up'}
        </button>
        <Link to={signInLink} className="ts-btn ts-btn--line">Log in</Link>
      </div>
    </form>
  );
}

export default SignUpForm;